import React, { useCallback, useMemo } from "react";
import { Feature, Polygon } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import { inParcel } from "fond/layers";
import {
  CombineButton,
  CutButton,
  DeleteButton,
  PolygonButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  SplitButton,
  ToolbarConfig,
  UncombineButton,
  UndoButton,
  UnionButton,
} from "fond/map/Toolbar";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";

import parcelDrawStyles from "./parcelDrawStyles";

const defaultOptions = {
  snap: true,
};

const sources: FeatureIdentifier[] = [{ source: inParcel }];

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PolygonButton key="drawPolygon" />],
  },
  {
    id: "modifyTools",
    type: "action",
    items: [
      <CutButton key="cut" />,
      <SplitButton key="split" />,
      <UnionButton key="union" />,
      <CombineButton key="combine" />,
      <UncombineButton key="uncombine" />,
    ],
  },
  {
    id: "snappingTools",
    type: "action",
    items: [<SnappingButton key="snap" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of parcels.
 * It uses the Mapbox Draw library to provide drawing functionality and
 * manages the state of the drawn features.
 */
const ParcelHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  const initialFeatures: Feature<Polygon>[] = useMemo(() => (data?.layers[inParcel].features || []) as Feature<Polygon>[], [data]);

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback((_: Feature) => ({ layerId: inParcel }), []);

  return (
    <DrawHandler
      startingMode="simple_select"
      initialFeatures={initialFeatures}
      sources={sources}
      config={toolbarConfig}
      options={defaultOptions}
      styles={parcelDrawStyles}
      getFeatureProperties={getFeatureProperties}
      disableHoverEffects
    />
  );
};

export default ParcelHandler;
